/**
 * Merchant pre-charge validation hook (PAY-1540).
 *
 * Merchants who must run their own backend checks before any money moves used to
 * run them in the tap handler, ahead of `submit()`. On Apple Pay / Google Pay
 * that consumes the browser's transient user activation (~5s in Chrome) and the
 * wallet sheet is rejected outright — `NotAllowedError` / `IntegrationError` —
 * whenever the checks run long, which is exactly what happens on slow networks.
 *
 * The requirement is validation-before-CHARGE, not validation-before-SHEET. With
 * the PAY-1538 architecture the sheet opens instantly from prepared state and the
 * charge only happens at `/confirm`, once the shopper has authorized. That leaves
 * a natural gap — after authorization, before the charge — that sits completely
 * outside the activation window. `beforeConfirm` is that gap made first-class:
 *
 *   tap -> sheet opens immediately -> shopper authorizes -> await beforeConfirm()
 *       -> reject: no /confirm, no charge | resolve: charge proceeds
 *
 * Single tap, validation strictly precedes the charge, activation window untouched.
 * Merchants can also kick their validation off at tap time and merely await the
 * in-flight promise here, which usually costs zero added latency.
 *
 * ## Why the timeout is not optional
 *
 * On the Stripe PaymentRequest rail Apple gives roughly 30 seconds between
 * authorization and `complete()` before it tears the sheet down itself. The
 * charge has to fit in that budget too. An unbounded hook would trade
 * `NotAllowedError` for a wallet sheet that hangs and then dies — a strictly
 * worse failure. So the hook always races a timer, and a timeout is treated as a
 * rejection: no charge, clear error.
 */

/**
 * What a merchant's hook may resolve with.
 *
 * Passing (charge proceeds): `void`/`undefined`, or `{ ok: true }`.
 * Blocking (no charge): `false`, or `{ ok: false }` — `error` is surfaced to the
 * shopper verbatim, so it should be customer-safe text.
 *
 * Throwing (or rejecting) also blocks — a merchant whose validation call throws
 * on a non-2xx response gets fail-closed behavior for free.
 *
 * NOTE for maintainers: PKJS also ships as a plain `<script>` tag, so a large
 * share of merchants get NO compile-time checking of this type. Anything a
 * reasonable JS developer would write to mean "reject" must actually block —
 * see `interpretDecision`. Widen that function, not just this type.
 */
// The common merchant hook is `async () => { await myCheck() }`, whose type is
// Promise<void>. Narrowing this to `undefined` would reject that signature and
// force every merchant to write an explicit `return undefined`.
// biome-ignore lint/suspicious/noConfusingVoidType: `void` is load-bearing — see above.
export type BeforeConfirmDecision = void | boolean | { ok: boolean; error?: string };

/** Merchant-supplied validation callback, awaited after authorization, before the charge. */
export type BeforeConfirmHook = () => BeforeConfirmDecision | Promise<BeforeConfirmDecision>;

/** Options every wallet submit path accepts for the hook. */
export type BeforeConfirmOptions = {
  /**
   * Runs after the shopper authorizes in the wallet sheet and BEFORE the charge.
   * Reject (or throw) to abort: no `/confirm` call is made and no money moves.
   */
  beforeConfirm?: BeforeConfirmHook;
  /**
   * Milliseconds to wait for `beforeConfirm` before aborting the payment.
   * Defaults to {@link DEFAULT_BEFORE_CONFIRM_TIMEOUT_MS}. Values that aren't a
   * finite positive number fall back to the default — the hook is never
   * unbounded (see the module comment for why).
   */
  beforeConfirmTimeoutMs?: number;
};

/**
 * Default hook budget, shared by every rail so `beforeConfirmTimeoutMs` means the
 * same thing everywhere it appears in the public API.
 *
 * The BINDING constraint is Apple's ~30s post-authorization window on the Stripe
 * PaymentRequest rail, which the charge itself also has to fit inside. But the
 * reason to have a budget at all is rail-neutral: it bounds how long a shopper
 * waits after they have committed to pay. On BNPL that wait is spent staring at a
 * blank popup with nothing authorized yet, which argues for a tighter budget than
 * the wallets need, not a looser one.
 */
export const DEFAULT_BEFORE_CONFIRM_TIMEOUT_MS = 10_000;

/**
 * Hard ceiling on `beforeConfirmTimeoutMs`, shared by every rail. Apple's
 * post-authorization window is roughly 30s and the charge itself has to fit inside
 * it too, so a merchant who asks for 60s would reproduce the exact hung-sheet
 * failure the timer exists to prevent.
 *
 * Rails without a sheet (BNPL, Link) are held to the same ceiling deliberately
 * rather than by oversight: a hook that outlives it has stopped being validation
 * and started being an outage, and forking the value per rail would fork the
 * meaning of a public API option merchants code against. Larger values are clamped
 * to this and warned about.
 */
export const MAX_BEFORE_CONFIRM_TIMEOUT_MS = 20_000;

/** Shown when a merchant aborts without supplying their own message. */
export const DEFAULT_BEFORE_CONFIRM_ERROR = "Payment validation failed. Please try again.";

/** Shown when the merchant's hook outlives its budget. */
export const BEFORE_CONFIRM_TIMEOUT_ERROR = "Payment validation timed out. Please try again.";

export type BeforeConfirmOutcome = { proceed: true } | { proceed: false; error: string };

/** Internal marker so a timeout is distinguishable from a merchant-thrown error. */
class BeforeConfirmTimeoutError extends Error {}

function toErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message.trim()) return err.message.trim();
  if (typeof err === "string" && err.trim()) return err.trim();
  return DEFAULT_BEFORE_CONFIRM_ERROR;
}

/**
 * Keys a merchant might plausibly use to mean "this validation passed". Any of
 * them present and falsy is read as a block.
 *
 * This list exists because PKJS ships as a plain `<script>` too: those merchants
 * get no compile-time checking, and `return false` / `return { valid: false }`
 * are at least as natural as the documented `{ ok: false }`. Reading an
 * unrecognised "reject" as a pass would charge a shopper the merchant meant to
 * block — the one outcome this whole feature exists to prevent.
 */
const VERDICT_KEYS = ["ok", "valid", "success", "allowed", "approved"] as const;

type Verdict = { blocked: false } | { blocked: true; error?: string };

/**
 * Resolve the hook's time budget: default when unusable, clamped at
 * {@link MAX_BEFORE_CONFIRM_TIMEOUT_MS} so the timer can always do its job.
 */
function resolveBudget(timeoutMs: number | undefined): number {
  if (typeof timeoutMs !== "number" || !Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return DEFAULT_BEFORE_CONFIRM_TIMEOUT_MS;
  }
  if (timeoutMs > MAX_BEFORE_CONFIRM_TIMEOUT_MS) {
    console.warn(
      `[PaymentKit] beforeConfirmTimeoutMs=${timeoutMs} exceeds the ${MAX_BEFORE_CONFIRM_TIMEOUT_MS}ms ceiling ` +
        "(a wallet sheet would time out on its own first, and on any rail a shopper should not " +
        "be left waiting this long after committing to pay) — clamping.",
    );
    return MAX_BEFORE_CONFIRM_TIMEOUT_MS;
  }
  return timeoutMs;
}

/**
 * Interpret whatever the merchant's hook resolved with.
 *
 * Deliberately asymmetric: blocking is recognised generously, passing is the
 * default only for values that clearly aren't a rejection. An unrecognised
 * object (e.g. a merchant returning their raw API response on success) passes,
 * but warns — silently blocking those would break working integrations.
 */
function interpretDecision(decision: unknown): Verdict {
  // `false` — the most natural plain-JS idiom for "reject".
  if (decision === false) return { blocked: true };
  if (decision === true || decision === undefined || decision === null) return { blocked: false };

  if (typeof decision === "object") {
    const record = decision as Record<string, unknown>;
    for (const key of VERDICT_KEYS) {
      if (key in record) {
        if (record[key]) return { blocked: false };
        const error = typeof record.error === "string" ? record.error : undefined;
        return { blocked: true, error };
      }
    }
    console.warn(
      "[PaymentKit] beforeConfirm resolved with an object carrying no recognised verdict key " +
        `(${VERDICT_KEYS.join("/")}) — treating it as a PASS. Return \`false\` or \`{ ok: false }\` to block the charge.`,
    );
    return { blocked: false };
  }

  // Any other primitive (number, string, symbol): not a recognised rejection.
  return { blocked: false };
}

/**
 * Run the merchant's pre-charge hook and decide whether the charge may proceed.
 *
 * Fail-closed by construction: every abnormal outcome (explicit rejection, throw,
 * rejected promise, timeout) resolves to `proceed: false`. Callers MUST NOT call
 * their `/confirm` endpoint unless this resolves `proceed: true`.
 *
 * Never throws — callers can treat it as a pure decision.
 */
export async function runBeforeConfirm(
  hook: BeforeConfirmHook | undefined,
  timeoutMs?: number,
): Promise<BeforeConfirmOutcome> {
  // No hook supplied: behave exactly as before this feature existed.
  if (!hook) return { proceed: true };

  const budget = resolveBudget(timeoutMs);

  // Hoisted out of the race so the losing side can be neutralised. Without the
  // no-op catch, a hook that rejects AFTER the timer already won surfaces as a
  // global `unhandledrejection` — console noise plus a spurious error-tracking
  // event on every timed-out validation.
  const hookPromise = Promise.resolve().then(hook);
  hookPromise.catch(() => {});

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const decision = await Promise.race([
      // Promise.resolve().then() also routes a SYNCHRONOUS throw from the hook
      // into the catch below instead of escaping this function.
      hookPromise,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(new BeforeConfirmTimeoutError()), budget);
      }),
    ]);

    const verdict = interpretDecision(decision);
    if (verdict.blocked) {
      return { proceed: false, error: verdict.error?.trim() || DEFAULT_BEFORE_CONFIRM_ERROR };
    }
    return { proceed: true };
  } catch (err) {
    if (err instanceof BeforeConfirmTimeoutError) {
      console.warn(`[PaymentKit] beforeConfirm exceeded ${budget}ms — aborting before any charge.`);
      return { proceed: false, error: BEFORE_CONFIRM_TIMEOUT_ERROR };
    }
    return { proceed: false, error: toErrorMessage(err) };
  } finally {
    // Always clear, including on the happy path, so a pending timer can't keep
    // the page (or a jsdom test) alive after the decision is made.
    if (timer) clearTimeout(timer);
  }
}
